import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const LoadingSkeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
  lines = 1,
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 rounded';
  
  const variantClasses = {
    text: 'h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  if (variant === 'text' && lines > 1) {
    return (
      <div className={className}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={`${baseClasses} ${variantClasses.text} mb-2`}
            style={{
              width: i === lines - 1 ? '80%' : width || '100%',
              height: height || '1rem',
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={{ width: width || '100%', height: height || '1rem' }}
      aria-label="Loading"
      role="status"
    />
  );
};

// Pre-built skeleton components
export const CardSkeleton: React.FC = () => (
  <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-sm">
    <LoadingSkeleton variant="rectangular" height="2rem" className="mb-4" />
    <LoadingSkeleton variant="text" lines={3} />
  </div>
);

export const DashboardSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 p-6">
    <div className="max-w-7xl mx-auto">
      <LoadingSkeleton variant="rectangular" height="3rem" className="mb-8" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[1, 2, 3].map((i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
      <CardSkeleton />
    </div>
  </div>
);

export const TableSkeleton: React.FC<{ rows?: number; cols?: number }> = ({
  rows = 5,
  cols = 4,
}) => (
  <div className="bg-white rounded-2xl p-6 border-2 border-gray-200">
    <LoadingSkeleton variant="rectangular" height="2rem" className="mb-4" />
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex space-x-4">
          {Array.from({ length: cols }).map((_, j) => (
            <LoadingSkeleton
              key={j}
              variant="rectangular"
              height="1.5rem"
              className="flex-1"
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

